import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type'> {
  value: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  error?: boolean;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, value, onChange, onBlur, error, disabled, placeholder, ...props }, ref) => {
    const [revealed, setRevealed] = React.useState(false);

    return (
      <div className="relative box-border h-10 w-full min-w-0 max-w-full">
        <Input
          ref={ref}
          type={revealed ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            'box-border h-10 w-full min-w-0 max-w-full border border-solid border-[var(--inv-border)] py-0 pr-10',
            // Hide Edge/IE native reveal so only our eye toggle shows
            '[&::-ms-reveal]:hidden [&::-ms-clear]:hidden',
            error && 'border-[#fecdca] focus-visible:ring-[#fecdca]',
            className,
          )}
          {...props}
        />
        <Button
          type="button"
          variant="ghost"
          tabIndex={-1}
          className="absolute right-3 top-1/2 z-[1] -translate-y-1/2 border-0 bg-transparent p-0 text-[var(--inv-text-muted)] hover:text-[var(--inv-text)]"
          onClick={() => setRevealed(v => !v)}
          aria-label={revealed ? 'Hide password' : 'Show password'}>
          {revealed ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
